import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { LeadWithTags, LeadStatus, LeadType } from '../../types/crm';

interface LeadsTableProps {
  leads: LeadWithTags[];
  isLoading?: boolean;
}

const STATUS_STYLES: Record<LeadStatus, string> = {
  NEW: 'bg-blue-50 text-blue-700',
  CONTACTED: 'bg-amber-50 text-amber-700',
  IN_DISCUSSION: 'bg-cyan-50 text-cyan-700',
  WON: 'bg-emerald-50 text-emerald-700',
  LOST: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'Neu',
  CONTACTED: 'Kontaktiert',
  IN_DISCUSSION: 'In Gespräch',
  WON: 'Gewonnen',
  LOST: 'Verloren',
};

const TYPE_STYLES: Record<LeadType, string> = {
  CUSTOMER: 'bg-primary/10 text-primary',
  PARTNER: 'bg-rose-50 text-rose-700',
};

const TYPE_LABELS: Record<LeadType, string> = {
  CUSTOMER: 'Kunde',
  PARTNER: 'Partner',
};

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min.`;
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  if (diffDays === 1) return 'Gestern';
  if (diffDays < 7) return `vor ${diffDays} Tagen`;
  return date.toLocaleDateString('de-DE');
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function LeadsTable({ leads, isLoading }: LeadsTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {leads.map((lead) => (
          <div
            key={lead.id}
            onClick={() => navigate(`/leads/${lead.id}`)}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 cursor-pointer hover:shadow-md transition-shadow active:bg-gray-50 dark:active:bg-slate-700"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">{lead.full_name}</h3>
                {lead.email && (
                  <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{lead.email}</p>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${TYPE_STYLES[lead.lead_type]}`}
              >
                {TYPE_LABELS[lead.lead_type]}
              </span>
              <span
                className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${STATUS_STYLES[lead.status]}`}
              >
                {STATUS_LABELS[lead.status]}
              </span>
              {lead.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-xs"
                >
                  {tag.name}
                </span>
              ))}
              {lead.tags.length > 2 && (
                <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-xs">
                  +{lead.tags.length - 2}
                </span>
              )}
            </div>
            {(lead.source || lead.last_activity_at) && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
                {lead.source && <span>{lead.source}</span>}
                {lead.last_activity_at && <span>{formatRelativeTime(lead.last_activity_at)}</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-slate-700">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Typ
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Quelle
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden lg:table-cell">
                  Letzte Aktivität
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden lg:table-cell">
                  Erstellt
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{lead.full_name}</p>
                      {lead.email && (
                        <p className="text-sm text-gray-500 dark:text-slate-400">{lead.email}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${TYPE_STYLES[lead.lead_type]}`}
                    >
                      {TYPE_LABELS[lead.lead_type]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${STATUS_STYLES[lead.status]}`}
                    >
                      {STATUS_LABELS[lead.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {lead.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-xs"
                        >
                          {tag.name}
                        </span>
                      ))}
                      {lead.tags.length > 2 && (
                        <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-xs">
                          +{lead.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-slate-400">
                    {lead.source || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400 hidden lg:table-cell">
                    {formatRelativeTime(lead.last_activity_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400 hidden lg:table-cell">
                    {formatDate(lead.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
